import * as THREE from 'three'
import {
  HintIndex,
  bestMatchingHint,
  buildGlbNameMapping,
  distinctAncestorNames,
  type MeshHint,
} from '@/utils/glbMeshMatching'
import { baseBoxOf, occurrenceNode, sortOccurrencesByPosition } from '@/utils/glbOccurrences'
import { computeExplodeOffsets, isFastenerName } from '@/utils/explodeGeometry'
import {
  clonePose,
  easeInOut,
  identityPose,
  isIdentityPose,
  posesEqual,
  type Pose,
} from '@/utils/pose'
import {
  pivotOffsetMatrix,
  poseFromStored,
  storedFromPose,
  type StoredArticleTransform,
} from '@/utils/poseMatrix'

// A transformable article: a mesh hint enriched with the stable article id the transform is keyed by.
export type ArticleTransformHint = MeshHint & { articleId: string }

export type { StoredArticleTransform }

type MeshBase = {
  localBase: THREE.Matrix4
  parentWorld: THREE.Matrix4
  parentWorldInv: THREE.Matrix4
}

type InstanceMeta = { articleNumber: string; articleId: string; index: number; editable: boolean }
type Anim = { from: Pose; to: Pose; start: number; duration: number }

// Aspect ratio above which a part is treated as elongated (screw/pin/shaft) and explodes along its own axis.
const ELONGATED_RATIO = 1.4
const ANIM_MS = 450

function instanceKey(articleNumber: string, index: number): string {
  return `${articleNumber} ${index}`
}

/**
 * Manages per-article-instance move/rotate manipulations (exploded views) on a loaded GLB model.
 *
 * Each physical occurrence of an article (e.g. each of four identical screws) is a separate instance that
 * can be moved independently. Instances are ordered deterministically by their base position and addressed
 * by `(articleNumber, index)` in the viewer and by `(articleId, instanceIndex)` when persisted.
 *
 * Offsets keep each mesh in place in the scene graph (so the name-based highlighting is undisturbed) and
 * rewrite its local matrix so its world matrix becomes `A · baseWorldMatrix`, where `A` is built about the
 * instance pivot. On a step switch only the instances whose offset actually changed ease to their new pose;
 * everything else (and direct gizmo edits) is applied instantly.
 */
export function useArticleTransforms(
  allMeshes: THREE.Mesh[],
  meshAncestorNames: Map<THREE.Mesh, string[]>,
  onChange: () => void = () => {},
) {
  const targets = new Map<string, Pose>()
  const displayed = new Map<string, Pose>()
  const anims = new Map<string, Anim>()

  const meshBase = new Map<THREE.Mesh, MeshBase>()
  const displacedMeshes = new Set<THREE.Mesh>()
  const meshesByInstance = new Map<string, THREE.Mesh[]>()
  const instanceByMesh = new Map<THREE.Mesh, string>()
  const instanceMeta = new Map<string, InstanceMeta>()
  const pivots = new Map<string, THREE.Vector3>()
  const articleIdByNumber = new Map<string, string>()
  const numberByArticleId = new Map<string, string>()

  let hints: ArticleTransformHint[] = []
  let editableArticleIds = new Set<string>()
  let isEditableOccurrence: (articleId: string, meshes: THREE.Mesh[]) => boolean = () => true
  let lastHintsRef: ArticleTransformHint[] | null = null
  const explodeVectors = new Map<string, THREE.Vector3>()
  let explodeValid = false
  let transformSource: 'none' | 'manual' | 'saved' | 'explode' = 'none'

  const mA = new THREE.Matrix4()
  const mNext = new THREE.Matrix4()

  function articlesPresentInGlb(candidates: Set<string>): Set<string> {
    const present = new Set<string>()
    for (const m of allMeshes) {
      for (const n of meshAncestorNames.get(m) ?? []) if (candidates.has(n)) present.add(n)
    }
    return present
  }

  function captureBase() {
    meshBase.clear()
    displacedMeshes.clear()
    lastHintsRef = null
    explodeValid = false
    for (const m of allMeshes) {
      m.parent?.updateWorldMatrix(true, false)
      const parentWorld = m.parent ? m.parent.matrixWorld.clone() : new THREE.Matrix4()
      meshBase.set(m, {
        localBase: m.matrix.clone(),
        parentWorld,
        parentWorldInv: parentWorld.clone().invert(),
      })
      m.matrixAutoUpdate = false
    }
  }

  function baseWorld(m: THREE.Mesh): THREE.Matrix4 {
    const b = meshBase.get(m)
    if (!b) return m.matrixWorld.clone()
    return b.parentWorld.clone().multiply(b.localBase)
  }

  function resetMeshToBase(m: THREE.Mesh) {
    const base = meshBase.get(m)
    if (!base) return
    m.matrix.copy(base.localBase)
    m.matrixWorldNeedsUpdate = true
    displacedMeshes.delete(m)
    onChange()
  }

  function resetUnposedMeshes() {
    if (displacedMeshes.size === 0) return
    const posed = new Set<THREE.Mesh>()
    for (const key of displayed.keys())
      for (const m of meshesByInstance.get(key) ?? []) posed.add(m)
    for (const m of [...displacedMeshes]) if (!posed.has(m)) resetMeshToBase(m)
  }

  function groupMeshes() {
    const previousMeshes = new Set<THREE.Mesh>()
    for (const meshes of meshesByInstance.values()) for (const m of meshes) previousMeshes.add(m)

    meshesByInstance.clear()
    instanceByMesh.clear()
    instanceMeta.clear()
    pivots.clear()

    if (hints.length > 0) {
      const articles = new Set(hints.flatMap((h) => h.glbArticleNames ?? [h.articleNumber]))
      const known = articlesPresentInGlb(articles)
      const mapping = buildGlbNameMapping(allMeshes, meshAncestorNames, hints, known)
      const index = new HintIndex(hints, distinctAncestorNames(allMeshes, meshAncestorNames), known)

      const perArticle = new Map<string, Map<string, THREE.Mesh[]>>()
      for (const m of allMeshes) {
        const ancestors = meshAncestorNames.get(m)
        if (!ancestors) continue
        const best = bestMatchingHint(ancestors, index.candidatesFor(ancestors), mapping, known)
        if (!best) continue
        const articleSet = new Set(best.glbArticleNames ?? [best.articleNumber])
        const occ = occurrenceNode(m, articleSet, known)
        const occId = occ ? occ.uuid : `mesh:${m.uuid}`
        const byOcc = perArticle.get(best.articleNumber) ?? new Map<string, THREE.Mesh[]>()
        ;(byOcc.get(occId) ?? byOcc.set(occId, []).get(occId)!).push(m)
        perArticle.set(best.articleNumber, byOcc)
      }

      for (const [article, byOcc] of perArticle) {
        const occurrences = sortOccurrencesByPosition([...byOcc.values()], baseWorld)
        const articleId = articleIdByNumber.get(article) ?? ''
        occurrences.forEach((meshes, index) => {
          const key = instanceKey(article, index)
          meshesByInstance.set(key, meshes)
          for (const m of meshes) instanceByMesh.set(m, key)
          instanceMeta.set(key, {
            articleNumber: article,
            articleId,
            index,
            editable: editableArticleIds.has(articleId) && isEditableOccurrence(articleId, meshes),
          })
          const box = baseBoxOf(meshes, baseWorld)
          pivots.set(key, box.isEmpty() ? new THREE.Vector3() : box.getCenter(new THREE.Vector3()))
        })
      }
    }

    const currentMeshes = new Set<THREE.Mesh>()
    for (const meshes of meshesByInstance.values()) for (const m of meshes) currentMeshes.add(m)
    for (const m of previousMeshes) if (!currentMeshes.has(m)) resetMeshToBase(m)

    for (const key of [...displayed.keys()]) if (!meshesByInstance.has(key)) displayed.delete(key)
    for (const key of [...anims.keys()]) if (!meshesByInstance.has(key)) anims.delete(key)
    for (const key of [...targets.keys()]) if (!meshesByInstance.has(key)) targets.delete(key)
  }

  function pivotOf(key: string): THREE.Vector3 {
    return pivots.get(key) ?? new THREE.Vector3()
  }

  const IDENTITY_POSE = identityPose()

  function targetRef(key: string): Pose {
    return targets.get(key) ?? IDENTITY_POSE
  }

  function targetPose(key: string): Pose {
    const pose = targets.get(key)
    return pose ? clonePose(pose) : identityPose()
  }

  // Write an instance's pose onto its meshes: worldMatrix = [T(δ)·T(p)·R·T(-p)] · baseWorldMatrix.
  function renderInstance(key: string) {
    const meshes = meshesByInstance.get(key)
    if (!meshes) return
    const pose = displayed.get(key)
    if (!pose) mA.identity()
    else pivotOffsetMatrix(mA, pose, pivotOf(key))
    for (const m of meshes) {
      const base = meshBase.get(m)
      if (!base) continue
      mNext
        .copy(base.parentWorldInv)
        .multiply(mA)
        .multiply(base.parentWorld)
        .multiply(base.localBase)
      m.matrix.copy(mNext)
      m.matrixWorldNeedsUpdate = true
      if (pose) displacedMeshes.add(m)
      else displacedMeshes.delete(m)
    }
    onChange()
  }

  function commit(key: string, animate: boolean) {
    const target = targetRef(key)
    const from = displayed.get(key) ?? IDENTITY_POSE
    if (!animate || posesEqual(from, target)) {
      anims.delete(key)
      if (target === IDENTITY_POSE || isIdentityPose(target)) {
        if (displayed.delete(key)) renderInstance(key)
      } else {
        displayed.set(key, clonePose(target))
        renderInstance(key)
      }
      return
    }
    displayed.set(key, clonePose(from))
    anims.set(key, {
      from: clonePose(from),
      to: clonePose(target),
      start: performance.now(),
      duration: ANIM_MS,
    })
    onChange()
  }

  function commitAll(animate: boolean) {
    for (const key of meshesByInstance.keys()) commit(key, animate)
  }

  function tick(now: number) {
    if (anims.size === 0) return
    for (const [key, anim] of anims) {
      const pose = displayed.get(key)
      if (!pose) {
        anims.delete(key)
        continue
      }
      const t = anim.duration <= 0 ? 1 : Math.min((now - anim.start) / anim.duration, 1)
      const e = easeInOut(t)
      pose.delta.lerpVectors(anim.from.delta, anim.to.delta, e)
      pose.quat.copy(anim.from.quat).slerp(anim.to.quat, e)
      renderInstance(key)
      if (t >= 1) {
        anims.delete(key)
        if (isIdentityPose(anim.to)) displayed.delete(key)
      }
    }
  }

  function setPose(key: string, pose: Pose, animate: boolean) {
    if (isIdentityPose(pose)) targets.delete(key)
    else targets.set(key, pose)
    commit(key, animate)
  }

  function setHints(
    newHints: ArticleTransformHint[],
    newEditableArticleIds: Set<string>,
    newIsEditableOccurrence: (articleId: string, meshes: THREE.Mesh[]) => boolean = () => true,
  ) {
    editableArticleIds = newEditableArticleIds
    isEditableOccurrence = newIsEditableOccurrence
    explodeValid = false
    if (newHints === lastHintsRef) {
      for (const [key, meta] of instanceMeta)
        meta.editable =
          editableArticleIds.has(meta.articleId) &&
          isEditableOccurrence(meta.articleId, meshesByInstance.get(key) ?? [])
      return
    }
    lastHintsRef = newHints
    hints = newHints
    articleIdByNumber.clear()
    numberByArticleId.clear()
    for (const h of hints) {
      articleIdByNumber.set(h.articleNumber, h.articleId)
      numberByArticleId.set(h.articleId, h.articleNumber)
    }
    groupMeshes()
  }

  // Apply the step's saved transforms; instances without one ease back to their base placement.
  function applyStored(stored: StoredArticleTransform[], animate = true) {
    targets.clear()
    for (const t of stored) {
      const article = numberByArticleId.get(t.articleId)
      if (!article) continue
      const key = instanceKey(article, t.instanceIndex)
      targets.set(key, poseFromStored(t, pivotOf(key)))
    }
    transformSource = stored.length > 0 ? 'saved' : 'none'
    for (const key of meshesByInstance.keys()) commit(key, animate)
    resetUnposedMeshes()
  }

  function proxyStateFor(key: string): { position: THREE.Vector3; quaternion: THREE.Quaternion } {
    const pose = targetPose(key)
    return { position: pivotOf(key).clone().add(pose.delta), quaternion: pose.quat }
  }

  function updateFromProxy(key: string, position: THREE.Vector3, quaternion: THREE.Quaternion) {
    setPose(key, { delta: position.clone().sub(pivotOf(key)), quat: quaternion.clone() }, false)
    transformSource = 'manual'
  }

  function computeExplodeVectors() {
    explodeVectors.clear()
    const editable = [...meshesByInstance.entries()]
      .filter(([key]) => instanceMeta.get(key)?.editable)
      .map(([key, meshes]) => ({
        key,
        meshes,
        elongated:
          isFastenerName(instanceMeta.get(key)?.articleNumber ?? '') ||
          meshes.some((m) => isFastenerName(m.name)),
      }))
    for (const [key, vector] of computeExplodeOffsets(editable, baseWorld, ELONGATED_RATIO)) {
      explodeVectors.set(key, vector)
    }
  }

  function autoExplode(factor: number) {
    if (!explodeValid) {
      computeExplodeVectors()
      explodeValid = explodeVectors.size > 0
    }
    targets.clear()
    for (const [key, vector] of explodeVectors) {
      targets.set(key, {
        delta: vector.clone().multiplyScalar(factor),
        quat: new THREE.Quaternion(),
      })
    }
    transformSource = 'explode'
    commitAll(false)
  }

  function reset() {
    targets.clear()
    transformSource = 'none'
    commitAll(false)
  }

  function reapplyPoses() {
    commitAll(false)
    resetUnposedMeshes()
  }

  function hasTransforms(): boolean {
    return targets.size > 0
  }

  // True when the model currently shows custom article positions — placed by hand this session or loaded
  // from saved transforms. An auto-explode would overwrite them, so the viewer warns before applying it.
  function hasCustomTransforms(): boolean {
    return targets.size > 0 && (transformSource === 'manual' || transformSource === 'saved')
  }

  function collectForSave(): StoredArticleTransform[] {
    const out: StoredArticleTransform[] = []
    for (const [key, pose] of targets) {
      const meta = instanceMeta.get(key)
      // Only the current step's own parts are saved against it.
      if (!meta || !meta.articleId || !meta.editable) continue
      out.push(storedFromPose(pose, pivotOf(key), meta))
    }
    return out
  }

  function instanceForMesh(mesh: THREE.Mesh): string | null {
    return instanceByMesh.get(mesh) ?? null
  }

  function isAnimating(): boolean {
    return anims.size > 0
  }

  function meshesForInstance(key: string): THREE.Mesh[] {
    return meshesByInstance.get(key) ?? []
  }

  function isEditableInstance(key: string): boolean {
    return instanceMeta.get(key)?.editable ?? false
  }

  function dispose() {
    targets.clear()
    displayed.clear()
    anims.clear()
    meshBase.clear()
    displacedMeshes.clear()
    meshesByInstance.clear()
    instanceByMesh.clear()
    instanceMeta.clear()
    pivots.clear()
    articleIdByNumber.clear()
    numberByArticleId.clear()
    hints = []
    lastHintsRef = null
    explodeVectors.clear()
    explodeValid = false
    transformSource = 'none'
  }

  return {
    captureBase,
    baseWorld,
    setHints,
    applyStored,
    reapplyPoses,
    tick,
    isAnimating,
    autoExplode,
    reset,
    hasTransforms,
    hasCustomTransforms,
    collectForSave,
    instanceForMesh,
    meshesForInstance,
    isEditableInstance,
    proxyStateFor,
    updateFromProxy,
    dispose,
  }
}
